import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { AlertCircle, ChevronRight, Play, Plus, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  Badge, Banner, Button, Card, ConfirmDialog, EmptyRow, KeyValues, MobileList, MobileListEmpty, MobileListSkeleton,
  MobileRow, Segmented, SkeletonRows, StatusPill, Switch, Tag,
} from '../kit';
import { useIsMobile } from '../../../hooks/use-mobile';
import {
  apiErrorMessage, fetchRefreshHistory, getFactRefreshStatus, patchOrganization, startFactRefresh,
} from '../../../utils/organizationApi';
import { fetchLocationsForOrg } from '../../../utils/locationApi';
import {
  buildRunRequest, computeNextRun, coverageLabel, defaultRunForm, describeRunWindow, FACT_COUNT, factLabel,
  failureMeaning, failureTitle, formatCount, formatDateTime, formatDuration, formatElapsed, groupHistoryRuns,
  INTERVAL_OPTIONS, intervalLabel, normalizeTime, RESULT_META, retryableFacts, scheduleFromOrg, schedulePatch,
  scheduleWindowLabel, shortFactNames, triggerLabel, validateRunForm, validateSchedule, WINDOW_UNITS, formatWhen,
} from './refreshModel';
import useRefreshTracker, { announceOutcome, liveElapsed } from './useRefreshTracker';
import { LocationPicker, TimezoneSelect } from './orgsUi';
import { phoneRefreshLabel } from './OrgCardList';

const HISTORY_PAGE = 300;

export default function OrgRefreshTab({ org, refreshState, onLiveStatus, onChanged }) {
  const isMobile = useIsMobile();
  const [runOptions, setRunOptions] = useState(false); // phones: custom window / full rebuild card
  const [editingSchedule, setEditingSchedule] = useState(false); // phones: schedule editor
  const [history, setHistory] = useState({ rows: [], loading: true, error: null, hasMore: false, nextBeforeId: null, loadingMore: false });
  const [locations, setLocations] = useState({ rows: [], loading: true });
  const [starting, setStarting] = useState(false);
  const [finished, setFinished] = useState(false);
  const announce = useRef(false);

  const loadHistory = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setHistory((h) => ({ ...h, loading: true, error: null }));
    try {
      const page = await fetchRefreshHistory({ orgId: org.id, limit: HISTORY_PAGE });
      setHistory((h) => {
        // keep older pages already loaded; replace the newest page
        const byId = new Map(h.rows.map((r) => [String(r.id), r]));
        page.rows.forEach((r) => byId.set(String(r.id), r));
        const merged = silent ? [...byId.values()] : page.rows;
        merged.sort((a, b) => Number(b.id) - Number(a.id));
        return {
          ...h,
          rows: merged,
          loading: false,
          error: null,
          hasMore: silent ? h.hasMore : page.hasMore,
          nextBeforeId: silent ? h.nextBeforeId : page.nextBeforeId,
        };
      });
    } catch (err) {
      setHistory((h) => ({ ...h, loading: false, error: apiErrorMessage(err, 'Could not load refresh history.') }));
    }
  }, [org.id]);

  const loadOlder = async () => {
    if (!history.nextBeforeId) return;
    setHistory((h) => ({ ...h, loadingMore: true }));
    try {
      const page = await fetchRefreshHistory({ orgId: org.id, limit: HISTORY_PAGE, beforeId: history.nextBeforeId });
      setHistory((h) => ({
        ...h,
        rows: [...h.rows, ...page.rows.filter((r) => !h.rows.some((x) => String(x.id) === String(r.id)))],
        hasMore: page.hasMore,
        nextBeforeId: page.nextBeforeId,
        loadingMore: false,
      }));
    } catch (err) {
      setHistory((h) => ({ ...h, loadingMore: false }));
      toast.error(apiErrorMessage(err, 'Could not load older runs.'));
    }
  };

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => {
    let cancelled = false;
    fetchLocationsForOrg(org.id)
      .then((rows) => { if (!cancelled) setLocations({ rows, loading: false }); })
      .catch(() => { if (!cancelled) setLocations({ rows: [], loading: false }); });
    return () => { cancelled = true; };
  }, [org.id]);

  const onFinish = useCallback((orgId, data) => {
    if (announce.current) {
      announce.current = false;
      announceOutcome(org.name, data);
    }
    setFinished(true);
    loadHistory({ silent: true });
    onChanged();
  }, [org.name, loadHistory, onChanged]);

  const { statuses, track } = useRefreshTracker(onFinish);
  const live = statuses[org.id] || null;

  // adopt a run already in flight (scheduled, or started from the list)
  useEffect(() => {
    let cancelled = false;
    getFactRefreshStatus(org.id)
      .then((s) => { if (!cancelled && s?.running) track(org.id, s); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [org.id, track]);

  useEffect(() => { onLiveStatus(live || (finished ? { running: false } : null)); }, [live, finished, onLiveStatus]);
  useEffect(() => () => onLiveStatus(null), [onLiveStatus]);

  // live history: refetch the newest page whenever another table lands
  const tablesDone = live?.tablesDone;
  useEffect(() => {
    if (tablesDone) loadHistory({ silent: true });
  }, [tablesDone, loadHistory]);

  const startRun = async (body, what = 'Refresh') => {
    setStarting(true);
    try {
      const res = await startFactRefresh(body);
      announce.current = true;
      setFinished(false);
      track(org.id, {
        running: true,
        startedAt: new Date().toISOString(),
        elapsedMs: 0,
        tablesDone: 0,
        tablesTotal: res?.tablesTotal || FACT_COUNT,
        fullBackfill: Boolean(body.fullBackfill),
        facts: res?.facts,
      });
      toast.success(`${what} started. It runs in the background; you can leave this page.`);
      return true;
    } catch (err) {
      if (err?.response?.status === 409) {
        toast('A refresh is already running for this organization.');
        track(org.id);
      } else {
        toast.error(apiErrorMessage(err, 'Could not start the refresh.'));
      }
      return false;
    } finally {
      setStarting(false);
    }
  };

  const running = live?.running ? live : null;
  const state = refreshState;
  const failed = state && !running && (state.key === 'failed' || state.key === 'partial');
  const retryFacts = failed ? retryableFacts(state) : null;

  const banner = failed ? (
    <Banner
      tone="bad"
      icon={AlertCircle}
      title={failureTitle(state)}
      action={(
        <div className="orgs-banner-actions">
          {retryFacts ? (
            <Button
              onClick={() => startRun(buildRunRequest(org, { mode: 'default', locationIds: [] }, { facts: retryFacts }), 'Retry')}
              disabled={starting}
            >
              Retry {shortFactNames(retryFacts).join(' & ').toLowerCase()} only
            </Button>
          ) : null}
          <Button
            variant={retryFacts ? undefined : 'primary'}
            icon={RefreshCw}
            onClick={() => startRun(buildRunRequest(org, { mode: 'default', locationIds: [] }), 'Refresh')}
            disabled={starting}
          >
            {retryFacts ? 'Retry all' : 'Retry'}
          </Button>
        </div>
      )}
    >
      {state.errorText ? <span className="orgs-err-mono orgs-block">{state.errorText}</span> : null}
      <span className="orgs-block">{failureMeaning(state, org)}</span>
    </Banner>
  ) : null;

  const runNowCard = (
    <RunNowCard
      org={org}
      locations={locations}
      running={running}
      starting={starting}
      onRun={startRun}
    />
  );

  if (isMobile) {
    return (
      <>
        {banner}
        <LastRefreshCard
          org={org}
          state={state}
          history={history}
          running={running}
          starting={starting}
          onRunNow={() => startRun(buildRunRequest(org, { mode: 'default', locationIds: [] }), 'Refresh')}
          optionsOpen={runOptions}
          onToggleOptions={() => setRunOptions((v) => !v)}
        />
        {runOptions ? runNowCard : null}
        {editingSchedule ? (
          <>
            <ScheduleCard org={org} onSaved={async () => { await onChanged(); setEditingSchedule(false); }} />
            <Button variant="ghost" className="ak-m-block" onClick={() => setEditingSchedule(false)}>Close schedule editor</Button>
          </>
        ) : (
          <ScheduleSummaryCard org={org} onEdit={() => setEditingSchedule(true)} />
        )}
        <HistoryList
          org={org}
          tz={state?.tz}
          history={history}
          running={running}
          onRetry={() => loadHistory()}
          onLoadOlder={loadOlder}
          locations={locations.rows}
        />
      </>
    );
  }

  return (
    <>
      {banner}

      <div className="ak-cols">
        <ScheduleCard org={org} onSaved={onChanged} />
        {runNowCard}
      </div>

      <HistoryFrame
        org={org}
        tz={state?.tz}
        history={history}
        running={running}
        onRetry={() => loadHistory()}
        onLoadOlder={loadOlder}
        locations={locations.rows}
      />
    </>
  );
}

// ── Schedule ────────────────────────────────────────────────────────────────

function ScheduleCard({ org, onSaved }) {
  const base = useMemo(() => scheduleFromOrg(org), [org]);
  const [draft, setDraft] = useState(base);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [addingTime, setAddingTime] = useState(false);
  const [newTime, setNewTime] = useState('07:00');
  const ids = { back: useId(), fwd: useId(), interval: useId(), time: useId() };

  const patch = useMemo(() => schedulePatch(base, draft), [base, draft]);
  const dirty = Object.keys(patch).length > 0;

  // follow the saved schedule when it changes and nothing is being edited
  const lastBase = useRef(base);
  useEffect(() => {
    if (lastBase.current !== base && !dirty) setDraft(base);
    lastBase.current = base;
  }, [base, dirty]);

  const set = (key, value) => {
    setDraft((d) => ({ ...d, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const addTime = () => {
    const t = normalizeTime(newTime);
    if (!t) return;
    set('times', [...new Set([...draft.times, t])].sort());
    setAddingTime(false);
  };

  const save = async () => {
    const next = validateSchedule(draft);
    setErrors(next);
    if (Object.keys(next).length) return;
    setSaving(true);
    try {
      await patchOrganization(org.id, patch);
      toast.success('Refresh schedule saved');
      await onSaved();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not save the schedule.'));
    } finally {
      setSaving(false);
    }
  };

  const preview = computeNextRun({
    dashboardRefreshEnabled: draft.enabled,
    dashboardRefreshTimesLocal: draft.times,
    dashboardRefreshTimezone: draft.timezone,
    dashboardRefreshIntervalMinutes: draft.mode === 'interval' ? draft.intervalMinutes : null,
  });

  return (
    <Card
      title="Schedule"
      actions={(
        <Switch
          checked={draft.enabled}
          onChange={(v) => set('enabled', v)}
          label={draft.enabled ? 'On' : 'Off'}
          title="Scheduled refresh"
        />
      )}
    >
      {!draft.enabled ? (
        <span className="ak-help">Scheduled refreshes are off for this organization. Run now still works.</span>
      ) : null}
      <div className="orgs-row">
        <span className="ak-label orgs-row-label">Runs</span>
        <div className="orgs-row-body">
          <Segmented
            mode="choice"
            label="Refresh mode"
            value={draft.mode}
            onChange={(v) => set('mode', v)}
            options={[{ value: 'times', label: 'At set times' }, { value: 'interval', label: 'Every N minutes' }]}
          />
        </div>
      </div>

      {draft.mode === 'times' ? (
        <div className="orgs-row">
          <span className="ak-label orgs-row-label">Times</span>
          <div className="orgs-row-body">
            {draft.times.map((t) => (
              <Tag key={t} removeLabel={`Remove ${t}`} onRemove={draft.times.length > 1 ? () => set('times', draft.times.filter((x) => x !== t)) : undefined}>
                <span className="ak-mono">{t}</span>
              </Tag>
            ))}
            {addingTime ? (
              <>
                <label className="ak-sr" htmlFor={ids.time}>New run time</label>
                <input
                  id={ids.time}
                  type="time"
                  className="ak-input orgs-time-input"
                  value={newTime}
                  autoFocus
                  onChange={(e) => setNewTime(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addTime(); if (e.key === 'Escape') setAddingTime(false); }}
                />
                <Button size="small" onClick={addTime}>Add</Button>
                <Button size="small" variant="ghost" onClick={() => setAddingTime(false)}>Cancel</Button>
              </>
            ) : (
              <Button size="small" variant="ghost" icon={Plus} onClick={() => setAddingTime(true)}>Add time</Button>
            )}
          </div>
          {errors.times ? <span className="ak-field-error" role="alert">{errors.times}</span> : null}
        </div>
      ) : (
        <div className="orgs-row">
          <label className="ak-label orgs-row-label" htmlFor={ids.interval}>Every</label>
          <div className="orgs-row-body">
            <select id={ids.interval} className="ak-select" style={{ width: 160 }} value={draft.intervalMinutes} onChange={(e) => set('intervalMinutes', Number(e.target.value))}>
              {INTERVAL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <span className="ak-help">Replaces the set times while on.</span>
          </div>
        </div>
      )}

      <div className="orgs-row">
        <span className="ak-label orgs-row-label">Timezone</span>
        <div className="orgs-row-body">
          <TimezoneSelect value={draft.timezone} onChange={(v) => set('timezone', v)} inline invalid={Boolean(errors.timezone)} ariaLabel="Refresh timezone" />
        </div>
      </div>

      <div className="orgs-row">
        <span className="ak-label orgs-row-label">Window</span>
        <div className="orgs-row-body">
          <input
            id={ids.back}
            className="ak-input orgs-num"
            type="number"
            min="1"
            max="3650"
            value={draft.daysBack}
            aria-invalid={Boolean(errors.daysBack)}
            onChange={(e) => set('daysBack', e.target.value)}
          />
          <label className="ak-help" htmlFor={ids.back}>days back</label>
          <input
            id={ids.fwd}
            className="ak-input orgs-num"
            type="number"
            min="0"
            max="3650"
            value={draft.daysForward}
            aria-invalid={Boolean(errors.daysForward)}
            onChange={(e) => set('daysForward', e.target.value)}
          />
          <label className="ak-help" htmlFor={ids.fwd}>days forward</label>
        </div>
        {errors.daysBack || errors.daysForward ? (
          <span className="ak-field-error" role="alert">{errors.daysBack || errors.daysForward}</span>
        ) : null}
      </div>

      <div className="orgs-card-foot">
        <span className="orgs-sub">Next run: {preview.label}</span>
        {dirty ? <Button size="small" variant="ghost" onClick={() => { setDraft(base); setErrors({}); }} disabled={saving}>Discard</Button> : null}
        <Button size="small" onClick={save} disabled={!dirty || saving}>{saving ? 'Saving…' : 'Save schedule'}</Button>
      </div>
    </Card>
  );
}

// ── Run now ─────────────────────────────────────────────────────────────────

function RunNowCard({ org, locations, running, starting, onRun }) {
  const [form, setForm] = useState(() => defaultRunForm(org));
  const [errors, setErrors] = useState({});
  const [confirmFull, setConfirmFull] = useState(false);
  const ids = { back: useId(), fwd: useId() };

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const locCount = locations.rows.length;
  const scope = form.mode !== 'full' && form.locationIds.length
    ? ` · ${form.locationIds.length} of ${locCount} locations`
    : '';
  const busy = Boolean(running) || starting;

  const run = async () => {
    const next = validateRunForm(form);
    setErrors(next);
    if (Object.keys(next).length) return;
    if (form.mode === 'full') { setConfirmFull(true); return; }
    await onRun(buildRunRequest(org, form), 'Refresh');
  };

  return (
    <Card title="Run now">
      <div className="orgs-row">
        <span className="ak-label orgs-row-label">Window</span>
        <div className="orgs-row-body">
          <Segmented
            mode="choice"
            label="Window"
            value={form.mode}
            onChange={(v) => set('mode', v)}
            options={[
              { value: 'default', label: 'Schedule default' },
              { value: 'custom', label: 'Custom' },
              { value: 'full', label: 'Full rebuild' },
            ]}
          />
        </div>
      </div>

      {form.mode === 'default' ? (
        <span className="ak-help">{scheduleWindowLabel(org)}, the same window as the schedule.</span>
      ) : null}
      {form.mode === 'custom' ? (
        <div className="orgs-row">
          <span className="orgs-row-label" aria-hidden="true" />
          <div className="orgs-row-body">
            <span className="orgs-num-unit">
              <input id={ids.back} className="ak-input orgs-num" type="number" min="1" value={form.backValue} aria-invalid={Boolean(errors.backValue)} aria-label="Back" onChange={(e) => set('backValue', e.target.value)} />
              <select className="ak-select" value={form.backUnit} aria-label="Back unit" onChange={(e) => set('backUnit', e.target.value)}>
                {WINDOW_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
              <span className="ak-help">back</span>
            </span>
            <span className="orgs-num-unit">
              <input id={ids.fwd} className="ak-input orgs-num" type="number" min="0" value={form.fwdValue} aria-invalid={Boolean(errors.fwdValue)} aria-label="Forward" onChange={(e) => set('fwdValue', e.target.value)} />
              <select className="ak-select" value={form.fwdUnit} aria-label="Forward unit" onChange={(e) => set('fwdUnit', e.target.value)}>
                {WINDOW_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
              </select>
              <span className="ak-help">forward</span>
            </span>
          </div>
          {errors.backValue || errors.fwdValue ? <span className="ak-field-error" role="alert">{errors.backValue || errors.fwdValue}</span> : null}
        </div>
      ) : null}
      {form.mode === 'full' ? (
        <span className="ak-help">
          Purges and rebuilds every fact table for the whole organization: about 25 months back and 13 months forward
          (labor: 18 months back, 1 month forward). It takes several minutes and always covers all locations.
        </span>
      ) : null}

      <div className="orgs-row">
        <span className="ak-label orgs-row-label">Locations</span>
        <div className="orgs-row-body">
          {form.mode === 'full' ? (
            <span className="ak-help">All {locCount} locations</span>
          ) : (
            <LocationPicker
              locations={locations.rows}
              loading={locations.loading}
              value={form.locationIds}
              onChange={(v) => set('locationIds', v)}
            />
          )}
        </div>
      </div>

      <div className="orgs-row">
        <span className="ak-label orgs-row-label">Covers</span>
        <div className="orgs-row-body">
          <span className="orgs-covers">{coverageLabel(org, form)} · {FACT_COUNT} fact tables{scope}</span>
        </div>
      </div>

      {running ? <RunProgress status={running} /> : null}

      <div className="orgs-card-foot">
        {running?.external ? <span className="orgs-sub">A scheduled refresh is running. Start another when it finishes.</span> : null}
        <Button variant={form.mode === 'full' ? 'danger-solid' : 'primary'} icon={busy ? RefreshCw : Play} className={busy ? 'orgs-busy' : undefined} onClick={run} disabled={busy}>
          {running ? 'Running…' : starting ? 'Starting…' : form.mode === 'full' ? 'Run full rebuild' : 'Run refresh'}
        </Button>
      </div>

      <ConfirmDialog
        open={confirmFull}
        title="Run a full rebuild?"
        confirmLabel="Run full rebuild"
        danger
        onCancel={() => setConfirmFull(false)}
        onConfirm={async () => {
          setConfirmFull(false);
          await onRun(buildRunRequest(org, form), 'Full rebuild');
        }}
      >
        <p style={{ margin: 0 }}>
          This deletes and rebuilds all report history for {org.name} ({coverageLabel(org, form)}) across all {locCount} locations.
          Dashboards and reports stay available but may show partial numbers until it finishes, usually several minutes.
        </p>
      </ConfirmDialog>
    </Card>
  );
}

function RunProgress({ status }) {
  const total = Number(status.tablesTotal) || FACT_COUNT;
  const done = Math.min(Number(status.tablesDone) || 0, total);
  const failedCount = Number(status.tablesFailed) || 0;
  const title = status.external ? 'Scheduled refresh running' : status.fullBackfill ? 'Full rebuild running' : 'Refresh running';
  const current = status.currentTable ? `Now: ${factLabel(status.currentTable)}` : status.lastTable ? `Last finished: ${factLabel(status.lastTable)}` : 'Starting…';
  return (
    <div className="orgs-progress" role="status" aria-live="polite">
      <div className="orgs-progress-head">
        <RefreshCw size={14} className="orgs-spin" aria-hidden="true" />
        <span>{title}</span>
        <span>{formatElapsed(liveElapsed(status))}</span>
      </div>
      {total <= 16 ? (
        <div className="orgs-bars" aria-hidden="true">
          {Array.from({ length: total }).map((_, i) => (
            <i key={i} className={i < done - failedCount ? 'orgs-on' : i < done ? 'orgs-fail' : ''} />
          ))}
        </div>
      ) : (
        <div className="orgs-bars" aria-hidden="true">
          <i className="orgs-on" style={{ flex: `0 0 ${Math.round((done / total) * 100)}%` }} />
          <i />
        </div>
      )}
      <div className="orgs-progress-meta">
        <span>{done} of {total} tables{failedCount ? ` · ${failedCount} failed` : ''}</span>
        <span>{formatCount(status.rowsInserted || 0)} rows</span>
        <span>{current}</span>
      </div>
    </div>
  );
}

// ── Phones: last refresh + schedule summary ─────────────────────────────────

function LastRefreshCard({ org, state, history, running, starting, onRunNow, optionsOpen, onToggleOptions }) {
  const zone = state?.tz || org.dashboardRefreshTimezone;
  const loadedRuns = useMemo(() => groupHistoryRuns(history.rows, zone), [history.rows, zone]);
  const last = loadedRuns[0] || state?.latest || null;
  const busy = Boolean(running) || starting;
  const when = running ? running.startedAt : (last?.startedAt || state?.lastRunAt);
  const pill = running
    ? <StatusPill tone="info" running>Running</StatusPill>
    : state ? <StatusPill tone={state.tone} title={state.detail}>{phoneRefreshLabel(state)}</StatusPill> : null;
  return (
    <Card title="Last refresh" actions={pill}>
      <KeyValues
        rows={[
          { key: 'time', label: running ? 'Started' : 'Time', value: <span className="ak-mono">{when ? formatWhen(when, zone) : '—'}</span> },
          {
            key: 'duration',
            label: 'Duration',
            value: <span className="ak-mono">{running ? formatElapsed(liveElapsed(running)) : formatDuration(last?.durationMs)}</span>,
          },
        ]}
      />
      {running ? <RunProgress status={running} /> : null}
      <Button
        variant="primary"
        icon={busy ? RefreshCw : Play}
        className={`ak-m-block${busy ? ' orgs-busy' : ''}`}
        onClick={onRunNow}
        disabled={busy}
        title={`Run the schedule's default window now (${scheduleWindowLabel(org)})`}
      >
        {running ? 'Running…' : starting ? 'Starting…' : 'Run now'}
      </Button>
      <button type="button" className="ak-link orgs-m-link" aria-expanded={optionsOpen} onClick={onToggleOptions}>
        {optionsOpen ? 'Hide run options' : 'Custom window or full rebuild'}
      </button>
    </Card>
  );
}

function ScheduleSummaryCard({ org, onEdit }) {
  const s = scheduleFromOrg(org);
  const next = computeNextRun(org);
  return (
    <Card title="Schedule" actions={<Button onClick={onEdit}>Edit</Button>}>
      <KeyValues
        rows={[
          { key: 'on', label: 'Scheduled refresh', value: s.enabled ? 'On' : 'Off' },
          {
            key: 'runs',
            label: s.mode === 'interval' ? 'Runs' : 'Times',
            value: <span className="ak-mono">{s.mode === 'interval' ? intervalLabel(s.intervalMinutes) : (s.times.join(', ') || '—')}</span>,
          },
          { key: 'tz', label: 'Timezone', value: String(s.timezone || '').replace(/_/g, ' ') },
          { key: 'window', label: 'Window', value: `${s.daysBack} days back · ${s.daysForward} forward` },
          { key: 'next', label: 'Next run', value: <span className="ak-mono">{next.label}</span> },
        ]}
      />
    </Card>
  );
}

/** Phones: history as tappable rows (mono date · trigger · tables | pill, duration). */
function HistoryList({ org, tz, history, running, onRetry, onLoadOlder, locations }) {
  const [open, setOpen] = useState(() => new Set());
  const zone = tz || org.dashboardRefreshTimezone;
  const runs = useMemo(() => groupHistoryRuns(history.rows, zone), [history.rows, zone]);
  const locName = useMemo(() => new Map(locations.map((l) => [Number(l.id), l.name])), [locations]);
  const runStart = running ? new Date(running.startedAt).getTime() - 60 * 1000 : null;
  const shown = running ? runs.filter((r) => r.startedAt.getTime() < runStart) : runs;
  const toggle = (id) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div className="ak-frame">
      <div className="orgs-frame-head">
        <h3>History</h3>
        <span className="ak-help">Times in {zone}</span>
      </div>
      {history.error ? (
        <div className="ak-error" role="alert">
          <AlertCircle size={16} aria-hidden="true" />
          <span style={{ flex: 1 }}>{history.error}</span>
          <Button size="small" onClick={onRetry}>Try again</Button>
        </div>
      ) : null}
      <MobileList label="Refresh history">
        {history.loading ? <MobileListSkeleton rows={4} lead={false} /> : null}
        {running ? (
          <MobileRow
            title={<span className="ak-mono">{formatDateTime(running.startedAt, zone)}</span>}
            sub={<span className="ak-m-shrink">{running.external ? 'Schedule' : running.fullBackfill ? 'Full rebuild' : triggerLabel('user', running.triggeredBy)} · {running.tablesDone || 0} / {running.tablesTotal || FACT_COUNT} tables</span>}
            aside={(
              <>
                <StatusPill tone="info" running>Running</StatusPill>
                <span className="ak-mono">{formatElapsed(liveElapsed(running))}</span>
              </>
            )}
          />
        ) : null}
        {!history.loading && !history.error && !running && shown.length === 0 ? (
          <MobileListEmpty title="No refresh runs recorded yet" body="History starts with the next refresh." />
        ) : null}
        {!history.loading && shown.map((run) => {
          const isOpen = open.has(run.id);
          const meta = RESULT_META[run.result];
          return (
            <MobileRow
              key={run.id}
              onClick={() => toggle(run.id)}
              expanded={isOpen}
              title={<span className="ak-mono">{formatDateTime(run.startedAt, zone)}</span>}
              sub={<span className="ak-m-shrink">{triggerLabel(run.trigger, run.triggeredBy)} · {run.tables} / {run.expected} tables</span>}
              aside={(
                <>
                  <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
                  <span className="ak-mono">{formatDuration(run.durationMs)}</span>
                </>
              )}
            >
              {isOpen ? (
                <div className="orgs-m-run">
                  <div className="orgs-m-run-meta ak-mono">{describeRunWindow(run)} · {formatCount(run.inserted)} rows</div>
                  {run.result !== 'done' && run.errorText ? <span className="orgs-err-mono">{run.errorText}</span> : null}
                  <ul className="orgs-m-tables">
                    {run.rows.map((r) => (
                      <li key={r.id}>
                        <span className="orgs-m-table-name">
                          {factLabel(r.factName)}
                          <span className="ak-muted"> · {r.locationId ? (locName.get(Number(r.locationId)) || `location ${r.locationId}`) : 'all locations'}</span>
                        </span>
                        <span className="ak-mono">{formatDuration(r.durationMs)}</span>
                        <StatusPill tone={r.status === 'succeeded' ? 'ok' : 'bad'} dot={false}>{r.status === 'succeeded' ? 'Done' : 'Failed'}</StatusPill>
                        {r.error ? <span className="orgs-err-mono orgs-m-table-err">{r.error}</span> : null}
                      </li>
                    ))}
                  </ul>
                  {run.missingFacts.length ? (
                    <span className="orgs-err-mono">Not refreshed in this run: {run.missingFacts.map(factLabel).join(', ')}</span>
                  ) : null}
                </div>
              ) : null}
            </MobileRow>
          );
        })}
      </MobileList>
      {history.hasMore && !history.loading ? (
        <div className="orgs-load-more">
          <Button className="ak-m-block" onClick={onLoadOlder} disabled={history.loadingMore}>{history.loadingMore ? 'Loading…' : 'Load older runs'}</Button>
        </div>
      ) : null}
    </div>
  );
}

// ── History ─────────────────────────────────────────────────────────────────

function HistoryFrame({ org, tz, history, running, onRetry, onLoadOlder, locations }) {
  const [open, setOpen] = useState(() => new Set());
  const zone = tz || org.dashboardRefreshTimezone;
  const runs = useMemo(() => groupHistoryRuns(history.rows, zone), [history.rows, zone]);
  const locName = useMemo(() => new Map(locations.map((l) => [Number(l.id), l.name])), [locations]);

  const runStart = running ? new Date(running.startedAt).getTime() - 60 * 1000 : null;
  const shown = running ? runs.filter((r) => r.startedAt.getTime() < runStart) : runs;
  const toggle = (id) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const COLS = 8;
  return (
    <div className="ak-frame">
      <div className="orgs-frame-head">
        <h3>History</h3>
        <span className="ak-help">Grouped by run · updates live while a run is in progress · times in {zone}</span>
      </div>
      {history.error ? (
        <div className="ak-error" role="alert">
          <AlertCircle size={16} aria-hidden="true" />
          <span style={{ flex: 1 }}>{history.error}</span>
          <Button size="small" onClick={onRetry}>Try again</Button>
        </div>
      ) : null}
      <div className="ak-scroll">
        <table className="ak-tbl orgs-hist">
          <thead>
            <tr>
              <th scope="col" className="ak-w-check"><span className="ak-sr">Expand</span></th>
              <th scope="col">Started</th>
              <th scope="col">Trigger</th>
              <th scope="col">Window</th>
              <th scope="col">Tables</th>
              <th scope="col">Rows replaced</th>
              <th scope="col">Duration</th>
              <th scope="col">Result</th>
            </tr>
          </thead>
          <tbody>
            {history.loading ? <SkeletonRows rows={4} columns={COLS} /> : null}
            {running ? (
              <tr>
                <td />
                <td className="ak-mono orgs-strong">{formatDateTime(running.startedAt, zone)}</td>
                <td><Badge>{running.external ? 'Schedule' : running.fullBackfill ? 'Full rebuild' : triggerLabel('user', running.triggeredBy)}</Badge></td>
                <td className="ak-mono">{running.fullBackfill ? 'Full rebuild' : 'In progress'}</td>
                <td className="ak-mono">{running.tablesDone || 0} / {running.tablesTotal || FACT_COUNT}</td>
                <td className="ak-mono">{formatCount(running.rowsInserted || 0)}</td>
                <td className="ak-mono">{formatElapsed(liveElapsed(running))}</td>
                <td><StatusPill tone="info" running>Running</StatusPill></td>
              </tr>
            ) : null}
            {!history.loading && !history.error && !running && shown.length === 0 ? (
              <EmptyRow colSpan={COLS} title="No refresh runs recorded yet" body="History starts with the next refresh." />
            ) : null}
            {!history.loading && shown.map((run) => {
              const isOpen = open.has(run.id);
              const meta = RESULT_META[run.result];
              return (
                <React.Fragment key={run.id}>
                  <tr className="ak-click" onClick={() => toggle(run.id)}>
                    <td>
                      <button
                        type="button"
                        className="ak-btn ak-ghost ak-icon ak-small"
                        aria-expanded={isOpen}
                        aria-label={`${isOpen ? 'Hide' : 'Show'} tables for the ${formatDateTime(run.startedAt, zone)} run`}
                        onClick={(e) => { e.stopPropagation(); toggle(run.id); }}
                      >
                        <span className={`orgs-caret${isOpen ? ' orgs-open' : ''}`}><ChevronRight size={14} aria-hidden="true" /></span>
                      </button>
                    </td>
                    <td className="ak-mono orgs-strong">{formatDateTime(run.startedAt, zone)}</td>
                    <td><Badge title={run.triggeredBy || undefined}>{triggerLabel(run.trigger, run.triggeredBy)}</Badge></td>
                    <td className="ak-mono">{describeRunWindow(run)}</td>
                    <td className="ak-mono">{run.tables} / {run.expected}</td>
                    <td className="ak-mono">{formatCount(run.inserted)}</td>
                    <td className="ak-mono">{formatDuration(run.durationMs)}</td>
                    <td>
                      <div className="orgs-result">
                        <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
                        {run.result !== 'done' && run.errorText ? <span className="orgs-err-mono">{run.errorText}</span> : null}
                      </div>
                    </td>
                  </tr>
                  {isOpen ? run.rows.map((r) => (
                    <tr key={r.id} className="orgs-sub-row">
                      <td />
                      <td colSpan={2}>
                        {factLabel(r.factName)}
                        <span className="ak-muted" style={{ fontSize: 12 }}>
                          {' · '}{r.locationId ? (locName.get(Number(r.locationId)) || `location ${r.locationId}`) : 'all locations'}
                        </span>
                      </td>
                      <td className="ak-mono">{r.windowStart && r.windowEnd ? `${r.windowStart} → ${r.windowEnd}` : '—'}</td>
                      <td />
                      <td className="ak-mono">{formatCount(r.rowsInserted)} <span className="ak-muted">(−{formatCount(r.rowsDeleted)})</span></td>
                      <td className="ak-mono">{formatDuration(r.durationMs)}</td>
                      <td>
                        <div className="orgs-result">
                          <StatusPill tone={r.status === 'succeeded' ? 'ok' : 'bad'} dot={false}>{r.status === 'succeeded' ? 'Done' : 'Failed'}</StatusPill>
                          {r.error ? <span className="orgs-err-mono">{r.error}</span> : null}
                        </div>
                      </td>
                    </tr>
                  )) : null}
                  {isOpen && run.missingFacts.length ? (
                    <tr className="orgs-sub-row">
                      <td />
                      <td colSpan={7} className="orgs-err-mono">Not refreshed in this run: {run.missingFacts.map(factLabel).join(', ')}</td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {history.hasMore && !history.loading ? (
        <div className="orgs-load-more">
          <Button size="small" onClick={onLoadOlder} disabled={history.loadingMore}>{history.loadingMore ? 'Loading…' : 'Load older runs'}</Button>
        </div>
      ) : null}
    </div>
  );
}
